import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import { ro, enUS } from 'date-fns/locale';
import i18n from '../i18n';

// Helper to get the current locale for date-fns
const getDateLocale = () => {
  const currentLang = i18n.language || 'ro';
  return currentLang === 'en' ? enUS : ro;
};

/**
 * Merge Tailwind classes with clsx
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format date to current locale
 */
export function formatDate(date: string | Date, formatStr = 'dd.MM.yyyy') {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, formatStr, { locale: getDateLocale() });
}

/**
 * Format date relative to now (e.g., "acum 2 ore" or "2 hours ago")
 */
export function formatRelativeDate(date: string | Date) {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return formatDistanceToNow(d, { addSuffix: true, locale: getDateLocale() });
}

/**
 * Format currency with current locale
 */
export function formatCurrency(
  amount: number,
  currency = 'RON',
  locale?: string
) {
  const currentLocale = locale || (i18n.language === 'en' ? 'en-US' : 'ro-RO');
  return new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Human-readable file size (B / KB / MB). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Format number with current locale
 */
export function formatNumber(num: number, decimals = 0) {
  const currentLocale = i18n.language === 'en' ? 'en-US' : 'ro-RO';
  return new Intl.NumberFormat(currentLocale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(num);
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Truncate text
 */
export function truncate(str: string, length: number) {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

/**
 * Get initials from name
 */
export function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Copy to clipboard
 */
export async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if device is mobile
 */
export function isMobile() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
}

/**
 * Get status color
 */
export function getStatusColor(status: string) {
  const colors: Record<string, string> = {
    // Payment status
    paid: 'badge-success',
    pending: 'badge-warning',
    overdue: 'badge-danger',
    cancelled: 'badge-secondary',
    
    // Task status
    todo: 'badge-secondary',
    in_progress: 'badge-primary',
    done: 'badge-success',
    
    // General
    active: 'badge-success',
    inactive: 'badge-secondary',
    error: 'badge-danger',
  };
  
  return colors[status] || 'badge-secondary';
}

/**
 * Get priority color
 */
export function getPriorityColor(priority: string) {
  const colors: Record<string, string> = {
    low: 'text-green-500',
    medium: 'text-yellow-500',
    high: 'text-red-500',
  };
  return colors[priority] || 'text-gray-500';
}

/**
 * Get priority emoji
 */
export function getPriorityEmoji(priority: string) {
  const emojis: Record<string, string> = {
    low: '🟢',
    medium: '🟠',
    high: '🔴',
  };
  return emojis[priority] || '⚪';
}
